import { formatMaybeDateTime } from '../SharedAccountBody';
import { FinIntellectModelCode } from '../FinIntellectModelCode';
import ComparisonSignalForgeBadge from './ComparisonSignalForgeBadge';
import type { ComparisonAccount } from './comparisonTypes';

/** Toggle when monitoring / artifact URLs are ready to surface. */
const SHOW_QUICK_LINKS = false;

/** Reserved for future quick links (fly.io health, Grafana, SignalForge, etc.). */
const QUICK_LINKS: ReadonlyArray<{ href: string; label: string }> = [];

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const DT_CLASS =
  'm-0 text-[11px] uppercase tracking-[0.04em] text-white/50';
const DD_CLASS = 'm-0 text-[13px] text-white/90';

const ComparisonRebalancingInfo = ({
  accounts,
}: {
  accounts: ComparisonAccount[];
}) => {
  if (accounts.length === 0) return null;

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-col">
        <h3 className="text-[1.05rem] font-semibold text-foreground">
          Rebalancing information
        </h3>
        <p className="mt-1 text-[0.85rem] text-white/55">
          Model routing and last rebalance per compared portfolio.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
        {accounts.map((acc) => {
          const tradingSince =
            acc.content?.tradingStartedAt ?? acc.content?.trackRecordStartedAt;
          return (
            <div
              key={acc.accountId}
              className="rounded-[10px] border border-white/[0.08] bg-white/[0.03] p-3"
            >
              <div className="mb-2.5 flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: acc.color }}
                  aria-hidden
                />
                <span className="text-[0.9rem] font-semibold">{acc.label}</span>
              </div>
              <dl className="m-0 grid gap-2">
                <div className="grid grid-cols-[1fr_1.2fr] items-center gap-2">
                  <dt className={DT_CLASS}>Model</dt>
                  <dd className={DD_CLASS}>
                    <FinIntellectModelCode model={acc.account.finintellectModel} />
                  </dd>
                </div>
                <div className="grid grid-cols-[1fr_1.2fr] items-center gap-2">
                  <dt className={DT_CLASS}>SignalForge</dt>
                  <dd className={DD_CLASS}>
                    <ComparisonSignalForgeBadge
                      enabled={!!acc.account.signal_forge_enabled}
                    />
                  </dd>
                </div>
                <div className="grid grid-cols-[1fr_1.2fr] items-center gap-2">
                  <dt className={DT_CLASS}>Last rebalanced</dt>
                  <dd className={DD_CLASS}>
                    {formatMaybeDateTime(
                      acc.account.lastRebalancedAt ?? undefined,
                    )}
                  </dd>
                </div>
                <div className="grid grid-cols-[1fr_1.2fr] items-center gap-2">
                  <dt className={DT_CLASS}>Trading since</dt>
                  <dd className={DD_CLASS}>{formatMaybeDateTime(tradingSince)}</dd>
                </div>
              </dl>
            </div>
          );
        })}
      </div>

      {SHOW_QUICK_LINKS && (
        <div hidden={QUICK_LINKS.length === 0}>
          <span className="mb-2 block text-[11px] uppercase tracking-[0.04em] text-white/50">
            Quick links
          </span>
          <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0">
            {QUICK_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] text-[color:var(--nq-blue-bright)] no-underline hover:underline"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComparisonRebalancingInfo;
